import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp
} from "firebase/firestore";
import { db, auth } from "../firebase";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";

const fmt = (v) => Number(v || 0).toFixed(2);

const getISTDate = () => {
  const now = new Date();
  const ist = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
  return ist.toISOString().slice(0, 10);
};

export default function CashierApproval() {
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) navigate("/");
    });
    return () => unsub();
  }, [navigate]);

  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("PENDING"); 
  const [loading, setLoading] = useState(true);
  const [exportDate, setExportDate] = useState("");

  const todayIST = getISTDate();

  useEffect(() => {
    async function loadPending() {
      setLoading(true);
      const q = query(
        collection(db, "salesman_ledger"),
        where("dateIST", "==", exportDate || todayIST),
        where("status", "==", "PENDING")
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPending(rows);
      setLoading(false);
    }
    loadPending();
  }, [exportDate, todayIST]);

  useEffect(() => {
    if (tab !== "HISTORY") return;
    async function loadHistory() {
      const q = query(
        collection(db, "salesman_ledger"),
        where("dateIST", "==", exportDate || todayIST),
        where("status", "in", ["APPROVED"])
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setHistory(rows);
    }
    loadHistory();
  }, [tab, exportDate, todayIST]);

  const approve = async (row) => {
    const ref = doc(db, "salesman_ledger", row.id);
    await updateDoc(ref, {
      status: "APPROVED",
      dayClose: true,
      approvedAt: serverTimestamp(),
      approvedBy: auth.currentUser?.email
    });
    setPending(pending.filter(p => p.id !== row.id));
  };

  const reject = async (row) => {
    const remark = prompt("Rejection remark (optional):") || "";
    const ref = doc(db, "salesman_ledger", row.id);
    await updateDoc(ref, {
      status: "REJECTED",
      dayClose: false,
      rejectionRemark: remark
    });
    setPending(pending.filter(p => p.id !== row.id));
  };

  const exportByDate = async () => {
    if (!exportDate) {
      alert("Please select date");
      return;
    }
    const q = query(
      collection(db, "salesman_ledger"),
      where("dateIST", "==", exportDate),
      where("status", "==", "APPROVED")
    );
    const snap = await getDocs(q);
    const rows = snap.docs.map(d => d.data());
    if (rows.length === 0) {
      alert("No data for selected date");
      return;
    }
    exportExcel(rows, exportDate);
  };

  const exportExcel = (rows, date) => {
    const data = (Array.isArray(rows) ? rows : []).map(p => ({
      Date: p.dateIST,
      Party: p.partyId,
      Opening: fmt(p.openingBalance),
      Sales: fmt(p.orderAmount),
      Online: fmt(p.onlineCollected),
      "₹500": p.denominations?.[500] || 0,
      "₹200": p.denominations?.[200] || 0,
      "₹100": p.denominations?.[100] || 0,
      "₹50": p.denominations?.[50] || 0,
      "₹20": p.denominations?.[20] || 0,
      "₹10": p.denominations?.[10] || 0,
      "₹5": p.denominations?.[5] || 0,
      "₹1": p.denominations?.[1] || 0,
      Cash: fmt(p.cashCollected),
      Closing: fmt(p.closingBalance),
      Status: p.status
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, date);
    XLSX.writeFile(wb, `cashier_${date}.xlsx`);
  };

  return (
    <Layout>
      <div className="layout-content">
        {/* HEADER */}
        <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
                <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>Cashier Governance</h1>
                <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Audit and authorize fiscal remediation across all distribution nodes.</p>
            </div>
            
            <div className="premium-card" style={{ padding: "8px 16px", display: "flex", gap: 16, alignItems: "center" }}>
                <input
                    type="date"
                    className="form-input"
                    value={exportDate || todayIST}
                    onChange={(e) => setExportDate(e.target.value)}
                    style={{ padding: "8px 12px", border: "none", background: "transparent", fontWeight: 700 }}
                />
                <button
                    onClick={exportByDate}
                    className="btn-secondary"
                    style={{ padding: "8px 20px", fontSize: 13 }}
                >
                    EXPORT MANIFEST
                </button>
            </div>
        </div>

        {/* TABS */}
        <div style={{ display: "flex", gap: 12, marginBottom: 32 }}>
            <button 
                className={tab === "PENDING" ? "btn-primary" : "btn-secondary"} 
                onClick={() => setTab("PENDING")}
                style={{ padding: "12px 32px", fontSize: 14, minWidth: 160 }}
            >
                Awaiting Audit ({pending.length})
            </button>
            <button 
                className={tab === "HISTORY" ? "btn-primary" : "btn-secondary"} 
                onClick={() => setTab("HISTORY")}
                style={{ padding: "12px 32px", fontSize: 14, minWidth: 160 }}
            >
                Authorized History
            </button>
        </div>

        {loading ? (
            <div className="premium-card" style={{ padding: 60, textAlign: "center" }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-muted)" }}>Synchronizing Ledger Data...</div>
            </div>
        ) : (
            <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
                <table className="enterprise-table">
                    <thead>
                        <tr>
                            <th style={{ paddingLeft: 24 }}>ENTITY NODE</th>
                            <th style={{ textAlign: "right" }}>OPENING</th>
                            <th style={{ textAlign: "right" }}>SALES</th>
                            <th style={{ textAlign: "right" }}>ONLINE</th>
                            <th style={{ textAlign: "center" }}>DENOM. BREAKDOWN</th>
                            <th style={{ textAlign: "right" }}>CASH REMITTED</th>
                            <th style={{ textAlign: "right" }}>STATIONARY BAL</th>
                            <th style={{ textAlign: "center", paddingRight: 24 }}>OPERATIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(tab === "PENDING" ? pending : history).length === 0 ? (
                            <tr>
                                <td colSpan="8" style={{ padding: 60, textAlign: "center", color: "var(--text-muted)" }}>No fiscal records detected for this node signature.</td>
                            </tr>
                        ) : (tab === "PENDING" ? pending : history).map(p => (
                            <tr key={p.id} style={{ background: p.status === "APPROVED" ? "rgba(16, 185, 129, 0.03)" : "transparent" }}>
                                <td style={{ paddingLeft: 24 }}>
                                    <div style={{ fontWeight: 800, color: "var(--primary)" }}>{p.partyId}</div>
                                    <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>REF: {p.id.slice(-8).toUpperCase()}</div>
                                </td>
                                <td style={{ textAlign: "right" }}>₹{fmt(p.openingBalance)}</td>
                                <td style={{ textAlign: "right" }}>₹{fmt(p.orderAmount)}</td>
                                <td style={{ textAlign: "right", color: "var(--success)", fontWeight: 700 }}>₹{fmt(p.onlineCollected)}</td>
                                <td style={{ textAlign: "center" }}>
                                    <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                        {[500, 200, 100, 50, 20, 10, 5, 1].map(d => (
                                            p.denominations?.[d] ? (
                                                <div key={d} style={{ fontSize: 9, fontWeight: 800, background: "var(--surface-hover)", padding: "2px 6px", borderRadius: 4, border: "1px solid var(--border)" }}>
                                                    {d}:{p.denominations[d]}
                                                </div>
                                            ) : null
                                        ))}
                                    </div>
                                </td>
                                <td style={{ textAlign: "right", fontWeight: 700 }}>₹{fmt(p.cashCollected)}</td>
                                <td style={{ textAlign: "right" }}>
                                    <div style={{ fontWeight: 900, color: "#f74605" }}>₹{fmt(p.closingBalance)}</div>
                                </td>
                                <td style={{ textAlign: "center", paddingRight: 24 }}>
                                    {tab === "PENDING" ? (
                                        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                                            <button 
                                                className="btn-primary" 
                                                style={{ padding: "8px 16px", fontSize: 11, background: "var(--success)" }}
                                                onClick={() => approve(p)}
                                            >
                                                AUTHORIZE
                                            </button>
                                            <button 
                                                className="btn-secondary" 
                                                style={{ padding: "8px 16px", fontSize: 11, color: "var(--danger)" }}
                                                onClick={() => reject(p)}
                                            >
                                                REJECT
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="badge badge-success">AUTHORIZED</div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
      </div>
    </Layout>
  );
}
